import React from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Badge from "@mui/material/Badge";
import LoadingButton from "@mui/lab/LoadingButton";

import paths from "../../constants/paths";
import { getProfileTarget } from "../../utils/profileTarget";
import { ReactComponent as CheckCircleIcon } from "../../assets/Icons/verify-outlined.svg";
import { useBasicInfo } from "../../contexts/BasicInfo";
import { treatment_program, LOADING } from "../../constants/common";
import { useAuth } from "../../contexts/Auth";
import { ReactComponent as AddIcon } from "../../assets/Icons/add.svg";
import { useNetwork } from "../../contexts/NetWork";
import { useAlert } from "../../contexts/Alert";
import { useHomePage } from "../../contexts/HomePage";
import { trackClickEvent } from "../../mixpanel";

const TherapistCardInfo = ({
  data,
  byTreatmentProgram = false,
  fetchSinglePostData = null,
}) => {
  const { data: basicInfo } = useBasicInfo();
  const { sendAlert } = useAlert();
  const { account_type } = useAuth();
  const { connectWithOthers, connectStatus } = useNetwork();
  const { fetchPostsStatus } = useHomePage();
  const info = data;

  // Use treatment_program_info if byTreatmentProgram is true, otherwise use info
  const userInfo = byTreatmentProgram
    ? data?.treatment_program_info
    : data?.info;

  const connect = async () => {
    try {
      await connectWithOthers({
        connected_user_id: userInfo.id,
        connected_user_type: byTreatmentProgram ? treatment_program : "user",
        account_type: account_type,
      });
      if (fetchSinglePostData) {
        await fetchSinglePostData();
      }
      sendAlert("Connected successfully!", "success");
    } catch (error) {
      sendAlert(error.response?.data?.message, "error");
    }
  };

  return (
    <Paper sx={{ py: 3 }}>
      <Box
        sx={{
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          px: 2,
        }}
      >
        <Badge
          color={
            userInfo.send_ref && userInfo.user_referral ? "success" : "error"
          }
          overlap="circular"
          badgeContent=" "
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          sx={{
            mb: 1.5,
            "& .MuiBadge-badge": {
              border: "1px solid #FFFFFF",
            },
          }}
        >
          <Avatar
            sx={{
              width: 72,
              height: 72,
            }}
            src={
              byTreatmentProgram ? data?.main_image : data?.user_profile_image
            }
          />
        </Badge>
        <Link
          href={
            info.profile_link ??
            (() => {
              const t = getProfileTarget(data, { by_treatment_program: byTreatmentProgram });
              return paths.userProfile(t.uuid, t.tid, t.accountType);
            })()
          }
          onClick={() => {
            trackClickEvent("Profile Viewed", {
              page: "Single Post",
              component: "Therapist Card",
              button: "View Profile",
            });
          }}
          variant="h5"
          mb={1.5}
          color="text.primary"
          textTransform="capitalize"
        >
          {byTreatmentProgram
            ? data?.treatment_program_info?.treatment_program?.facility_name
            : `${data.first_name || ""} ${data.last_name || ""}`}
        </Link>
        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          lineHeight="18px"
        >
          {userInfo?.headline}
        </Typography>
        {/* {data?.email_verified_at && (
          <Typography
            variant="body2"
            color="success.main"
            display="flex"
            alignItems="center"
            fontWeight={600}
            gap={0.5}
          >
            <CheckCircleIcon />
            Verified Account
          </Typography>
        )} */}
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        {basicInfo.id !== data.id && !data.is_connected ? (
          <LoadingButton
            variant="outlined"
            style={{ borderRadius: "25px", margin: "0px 20px" }}
            onClick={connect}
            startIcon={<AddIcon />}
            loading={connectStatus === LOADING || fetchPostsStatus === LOADING}
            disabled={connectStatus === LOADING || fetchPostsStatus === LOADING}
          >
            Connect
          </LoadingButton>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Link
              href={(() => {
                const t = getProfileTarget(data, { by_treatment_program: byTreatmentProgram });
                return paths.userProfile(t.uuid, t.tid, t.accountType);
              })()}
              onClick={() => {
                trackClickEvent("Profile Viewed", {
                  page: "Single Post",
                  component: "Therapists Card",
                  button: "View Full Profile",
                });
              }}
              variant="body2"
              mt={2}
              textTransform="capitalize"
            >
              View full profile
            </Link>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default TherapistCardInfo;
